import {useEffect, useLayoutEffect, useRef} from 'react';

import {useEagGridStore} from '../store/useEagGridStore';
import type {Filters} from '../types/Filters';
import {getPageFromUrl, setPageInUrl} from '../utils/url/getSearchParamsByGridCode';

type UsePageUrlSyncParams = {
  gridCode: string;
  page: number;
  filterValues: Filters;
  queryId: string;
};

/**
 * Owns the full page lifecycle for a single EagGrid instance:
 *
 * - **Phase 1 (mount):** reads page from `&page=gridCode:N` URL param and initialises the store
 *   (converts 1-based URL to 0-based store). Runs as `useLayoutEffect` so the store is updated
 *   before the fetch effect sees `offset`.
 *
 * - **Phase 2 (page change):** writes the current page to `&page=gridCode:N` via `replaceState`.
 *   Reads `store.getState().page` (not `params.page`) to avoid writing the stale React snapshot
 *   on mount.
 *
 * - **Phase 3 (filter/preset change):** resets page to 0 when `filterValues` or `queryId` changes
 *   after the grid has fully hydrated. Hydration is detected semantically: the grid is considered
 *   hydrated once `queryId` transitions from `''` to a real value (set by `useServerData.fetchPage`
 *   after the first data fetch). Until that point, all changes are initialization and must not
 *   reset the page. The `Boolean(prevQueryIdRef.current)` guard prevents the initial `'' → 'abc-123'`
 *   queryId transition from being treated as a preset switch.
 *
 *   This approach is immune to React 18 StrictMode double-invocation, extra re-renders, or any
 *   other scheduling variation — it tracks semantic readiness, not effect execution count.
 *
 * ## URL format
 * Page is stored as a separate top-level query param (not inside `dataGrids`):
 *   `?dataGrids=vehicle-opensearch,abc,1|&page=vehicle-opensearch:3`
 * Multiple grids:
 *   `?dataGrids=vehicle-opensearch,abc,1|customers,def,2|&page=vehicle-opensearch:3,customers:1`
 *
 * ## Why useLayoutEffect for Phase 1
 * Phase 1 MUST run as `useLayoutEffect` (not `useEffect`/`useOnMount`).
 * EagGridTable's data-fetching effect (`useEffect([offset, ...])`) uses `offset`
 * derived from `page`. If we restored page in a regular `useEffect`, the fetch
 * would run first with `offset=0` (wrong), triggering a wasted HTTP request.
 *
 * `useLayoutEffect` runs synchronously before any `useEffect`. When `setPage`
 * mutates the store, `useSyncExternalStore` triggers a synchronous re-render so
 * the fetch effect sees the correct offset immediately — single fetch on mount.
 */
export const usePageUrlSync = (params: UsePageUrlSyncParams) => {
  const store = useEagGridStore();
  const initializedRef = useRef(false);

  // Phase 1: Read page from URL on mount, initialize store (one-time).
  // useLayoutEffect so setPage fires BEFORE the fetchPage useEffect in EagGridTable.
  useLayoutEffect(() => {
    if (initializedRef.current) {
      return;
    }

    const urlPage = getPageFromUrl(params.gridCode);

    if (urlPage !== undefined && urlPage >= 1) {
      store.setPage(urlPage - 1);
    }

    initializedRef.current = true;
    // initializedRef guard ensures this runs only once even if deps technically change.
    // params.gridCode is stable per grid instance; store is ref-stable.
  }, [params.gridCode, store]);

  // Phase 2: Write page to URL on every page change (after initialization).
  // No queryId dependency — page param is independent of dataGrids.
  //
  // IMPORTANT: we read store.getState().page (not params.page) for the value written.
  // On mount, Phase 1 (useLayoutEffect) already set the correct page in the store,
  // but params.page is the stale React render snapshot (still 0). If we wrote
  // params.page + 1 on mount, we would overwrite the restored page with 1.
  // store.getState().page is always authoritative and up-to-date because useLayoutEffect
  // ran synchronously before this useEffect. params.page still serves as the trigger
  // dependency — it changes when the user clicks pagination — but the value we
  // serialize comes from the live store.
  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive URL write; fires on every pagination click; reads authoritative page from store.getState() (not params.page) to avoid stale React snapshot on mount
  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }

    setPageInUrl(params.gridCode, store.getState().page + 1);
  }, [params.page, params.gridCode, store]);

  // Phase 3: Reset page to 0 when filters or preset (queryId) change.
  //
  // Uses a semantic "hydrated" flag instead of a fragile execution counter.
  // The grid is considered hydrated once queryId transitions from '' to a real value
  // (set by useServerData.fetchPage after the first data fetch). Until that point,
  // all filterValues/queryId changes are part of initialization and must not reset
  // the page. This approach is immune to React 18 StrictMode double-invocation.
  //
  // queryId guard: Boolean(prevQueryIdRef.current) ensures the initial '' → 'abc-123'
  // transition (queryId resolving for the first time) is not treated as a preset switch.
  const hydratedRef = useRef(false);
  const prevFilterRef = useRef(params.filterValues);
  const prevQueryIdRef = useRef(params.queryId);
  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive page reset with semantic hydration detection; must fire on filterValues/queryId changes after hydration; immune to StrictMode double-invocation via hydratedRef
  useEffect(() => {
    const filtersChanged = prevFilterRef.current !== params.filterValues;
    const presetChanged =
      prevQueryIdRef.current !== params.queryId && Boolean(prevQueryIdRef.current);

    if (!hydratedRef.current) {
      // Detect hydration: queryId transitions from '' → real value.
      // This is the last initialization step (useServerData.fetchPage sets queryId).
      if (params.queryId !== '' && prevQueryIdRef.current === '') {
        hydratedRef.current = true;
      }
      prevFilterRef.current = params.filterValues;
      prevQueryIdRef.current = params.queryId;
      return;
    }

    if (filtersChanged || presetChanged) {
      store.setPage(0);
    }

    prevFilterRef.current = params.filterValues;
    prevQueryIdRef.current = params.queryId;
  }, [params.filterValues, params.queryId, store]);
};
